import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import { AnalyticsEvent } from 'utils/analytics/AnalyticsEvent'
import Box from '@mui/material/Box'
import { VisualAdminDTO, VisualClientDTO, VisualCreativeDTO } from 'models/visuals'
import { DetailFileAttachment } from './DetailFileAttachment.component'
import { DownloadAllFilesButton } from './DownloadAllFilesButton.component'
import { FC } from 'react'
import { GRAY_900 } from 'constants/styling/theme'
import { RendererErrorState } from './RendererErrorState.component'
import Stack from '@mui/material/Stack'
import { VisualFileType } from 'constants/visual'
import { ensureFilenameExtension } from 'utils/helpers'
import { logAnalyticsEvent } from 'utils/analytics/analyticsUtils'
import { logger } from 'utils/logging'
import { useAssignmentDetailDrawerWorkspaceId } from '../AssignmentDetailDrawer.context'
import { useAuth0 } from 'utils/auth'
import { useGalleryAssignment } from 'components/pages/Gallery/_main/contexts/GalleryAssignment.context'
import { useGetAssignmentVisualByTypeName, useGetAssignmentVisuals, useGetAssignmentVisualsWithWorkspace } from 'dataQueries/assignmentVisuals.query'
import { useTranslation } from 'react-i18next'

interface Props {
  assignmentId: string
}

/**
 * Self-contained section that fetches and displays uploaded visuals for an assignment.
 * Always shows the centered title. Displays a loading spinner, error state with retry, or file list.
 * @example
 * <UploadedVisualsSection assignmentId={assignment.id} />
 */
export const UploadedVisualsSection: FC<Props> = ({ assignmentId }) => {

  const { t } = useTranslation(['gallery'])
  const { roles } = useAuth0()
  const workspaceId = useAssignmentDetailDrawerWorkspaceId()

  const adminVisualsQuery = useGetAssignmentVisuals(roles.isAdmin ? assignmentId : '', VisualFileType.RAW)
  const workspaceVisualsQuery = useGetAssignmentVisualsWithWorkspace(roles.isAdmin ? '' : assignmentId, workspaceId, VisualFileType.RAW)

  const isLoading = roles.isAdmin ? adminVisualsQuery.isLoading : workspaceVisualsQuery.isLoading
  const isError = roles.isAdmin ? adminVisualsQuery.isError : workspaceVisualsQuery.isError
  const refetch = roles.isAdmin ? adminVisualsQuery.refetch : workspaceVisualsQuery.refetch
  const files = roles.isAdmin ? adminVisualsQuery.data?.data : workspaceVisualsQuery.data

  const { realtyId } = useGalleryAssignment()
  const getVisualByName = useGetAssignmentVisualByTypeName()

  const handleDownload = (file: VisualAdminDTO | VisualClientDTO | VisualCreativeDTO) => {
    getVisualByName.mutate(
      {
        assignmentId,
        workspaceId,
        visualType: VisualFileType.RAW,
        visualName: file.name,
      },
      {
        onSuccess: (response) => {
          const url = response.data.url
          if (!url.startsWith('https://')) {
            logger.warn('Skipped visual download due to non-https url', undefined, { context: { component: 'UploadedVisualsSection', metadata: { assignmentId, fileName: file.name, urlPrefix: url.slice(0, 8) } } })
            return
          }

          window.open(url, '_blank', 'noopener,noreferrer')

          logAnalyticsEvent(AnalyticsEvent.ASSIGNMENT_DETAIL_DOWNLOAD_VISUAL, {
            assignmentId,
            realtyId,
            planId: workspaceId,
            fileName: file.name,
            originalFileName: file.originalName,
          })
        },
        onError: (error) => {
          logger.error('Failed to resolve visual signed url', error, { context: { component: 'UploadedVisualsSection', metadata: { assignmentId, fileName: file.name } } })
        },
      }
    )
  }

  if (isLoading || (!isError && !files?.visuals.length)) return null

  return (
    <Stack alignItems="center" paddingBottom="2.4rem">
      <Box
        fontSize="1.6rem"
        fontWeight={600}
        color={GRAY_900}
        marginBottom="1.6rem"
        textAlign="center"
      >
        {t('gallery:assignment_details.uploaded_files')}
      </Box>

      {isError && <RendererErrorState onRetry={refetch} />}

      {!isError && files &&

        <Stack width="100%" gap={0.8}>
          {files?.visuals.map((file) => (
            <DetailFileAttachment
              key={file.name}
              fileName={file.originalName ?? file.name}
              onDownload={() => handleDownload(file)}
              icon={<ImageOutlinedIcon sx={{ color: GRAY_900, fontSize: '2rem' }} />}
            />
          ))}
        </Stack>
      }

      {!isError && !!files?.visuals.length &&

        <Box marginTop="1.6rem">

          <DownloadAllFilesButton
            zipName={assignmentId}
            getFiles={async () => {
              const visuals = files?.visuals ?? []
              return Promise.all(
                visuals.map(async (visual) => {
                  const response = await getVisualByName.mutateAsync({
                    assignmentId,
                    workspaceId,
                    visualType: VisualFileType.RAW,
                    visualName: visual.name,
                  })

                  // RAW uploads have no `originalName` and `name` lacks an extension — fall back to the signed URL.
                  const baseFilename = visual.originalName ?? visual.name
                  return {
                    url: response.data.url,
                    filename: ensureFilenameExtension(baseFilename, { url: response.data.url }),
                  }
                })
              )
            }}
          />

        </Box>
      }

    </Stack>
  )
}
